import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Button from "@mui/material/Button";
import isEmpty from "lodash/isEmpty";

import { useSelectAll } from "../../hooks/useSelectAll";
import { getLocalTime, dateFormat } from "../../utils/Time";
import { notify, notifySimple } from "../../themes/components/Notifications";
import useModalStyles from "../../styles/Modals";
import useButtonStyles from "../../styles/Buttons";
import useSharedStyles from "../../styles/Shared";

import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "../Icons/FileDownloadIcon";
import {
  fetchProjectResponses,
  updateResponses,
  downloadProjectResponses,
} from "../../api/Project";
import { Badge, Chip, InputAdornment, Popover, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { COLORS } from "../../themes/Base";
import TuneIcon from "@mui/icons-material/Tune";
import CustomCheckbox from "../../components/Form/CustomCheckbox";
import ReplayIcon from "@mui/icons-material/Replay";
import ChevronDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronUpIcon from "@mui/icons-material/KeyboardArrowUp";
import StickyPagination from "../../components/StickyPagination";
import ArrowDownIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpIcon from "@mui/icons-material/ArrowUpward";

type Props = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  baseName: string;
  projectId: string;
  projectSupplier?: ProjectSupplier;
  totalResponses: number;
};

type DeviceForensiqResponseProperties = {
  forensiqScore: string;
  isBot: string;
  isEventUnique: string;
};

const statusOptions = [
  { value: "entered", label: "Entered", bgcolor: "#EAEEFF", color: "#2D50FF" },
  { value: "completed", label: "Completed", bgcolor: "#E9F8F1", color: "#15663F" },
  { value: "quota_full", label: "Closed", bgcolor: "#F9F5FC", color: "#65318A" },
  { value: "screened_out", label: "Screened Out", bgcolor: "#FDF6EA", color: "#83591A" },
  { value: "terminated", label: "Terminated", bgcolor: "#FFECEB", color: "#B53129" },
  { value: "test", label: "Test", bgcolor: "#EAEAED", color: "#4F4F52" },
];

const ResponsesModal: React.FC<Props> = ({
  open,
  setOpen,
  baseName,
  projectId,
  projectSupplier,
  totalResponses,
}) => {
  const { classes: modalClasses } = useModalStyles();
  const { classes: buttonClasses, cx } = useButtonStyles();
  const { classes: sharedClasses } = useSharedStyles();

  const [statusFilter, setStatusFilter] = useState([]);
  const [searchPhrase, setSearchPhrase] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const filterOpen = Boolean(filterAnchorEl);
  const [sortBy, setSortBy] = useState<string>("inserted_at:desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [rows, setRows] = useState<ProjectResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // const [isLoadingPage, setLoadingPage] = useState(false);

  const [sortField, sortOrderValue] = sortBy.split(":");

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortBy(`${field}:${sortOrderValue === "asc" ? "desc" : "asc"}`);
    } else {
      setSortBy(`${field}:asc`);
    }
  };

  const renderSortArrow = (field: string) => {
    if (sortField === field) {
      return sortOrderValue === "asc" ? <ArrowDownIcon /> : <ArrowUpIcon />;
    }
    return <ArrowDownIcon />;
  };

  const fetchResponses = async () => {
    // setLoadingPage(true);
    try {
      const response = await fetchProjectResponses(projectId, {
        page: currentPage,
        limit: rowsPerPage,
        status: appliedFilters || undefined,
        search: debouncedSearch || undefined,
        sortField: sortBy.split(":")[0],
        sortOrder: sortBy.split(":")[1],
        projectSupplierId: projectSupplier ? projectSupplier?.id : undefined,
      });
      if (response && response.data && response.meta) {
        setRows(response.data);
        setTotal(response.meta?.total);
      }
    } catch (e) {
      console.error(e);
    } finally {
      // setLoadingPage(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchPhrase);
    }, 400); // 400ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [searchPhrase]);

  useEffect(() => {
    if (!open) return;
    void (async () => {
      await fetchResponses();
    })();
  }, [open, currentPage, rowsPerPage, appliedFilters, debouncedSearch, projectId, sortBy]);

  const {
    isChecked,
    selectedCount,
    selectedList,
    clearSelectedList,
    handleCheckboxClick,
    handleSelectAllClick,
  } = useSelectAll(rows);

  const handleUpdateResponses = async () => {
    try {
      const response = await updateResponses({ ids: selectedList, status: newStatus });
      const { messages } = response.projectResponseStatusBatchUpdate;

      if (!isEmpty(messages)) {
        notify(messages);
      } else {
        notifySimple(["Responses updated!"], "success");
        clearSelectedList();
        setNewStatus("");
        await fetchResponses();
      }
    } catch (err) {
      notifySimple(["Error updating project"], "error");
    }
  };

  const getDeviceForensiqResponseProperties = (response: {
    device_forensiq?: string;
  }): DeviceForensiqResponseProperties => {
    let forensiqScore = "N/A";
    let isBot = "N/A";
    let isEventUnique = "N/A";
    if (response.device_forensiq) {
      try {
        const forensiqData = JSON.parse(response.device_forensiq);
        forensiqScore = forensiqData.forensic?.marker?.score ?? "N/A";
        isBot = forensiqData.forensic?.marker?.isBot ? "Yes" : "No";
        isEventUnique = forensiqData.unique?.isEventUnique ? "Yes" : "No";
      } catch (error) {
        console.error("Error parsing device_forensiq JSON:", error);
      }
    }
    return {
      forensiqScore: forensiqScore,
      isBot: isBot,
      isEventUnique: isEventUnique,
    };
  };

  const handleStatusCheckbox = (status: string) => {
    setStatusFilter((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const renderStatusChip = (status) => (
    <Chip
      label={status.label}
      sx={{
        background: status.bgcolor,
        color: status.color,
        fontSize: 14,
        height: 24,
      }}
    />
  );

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={() => setOpen(false)}
      keepMounted={false}
      PaperProps={{
        className: sharedClasses.dialogModalBox,
      }}
    >
      <DialogTitle sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h3" sx={{ p: 0 }}>
            {projectSupplier ? (
              <>
                Responses - {projectSupplier.source.name}
                <span style={{ color: COLORS.textSecondary }}>
                  &nbsp;{projectSupplier?.country_code}
                </span>
              </>
            ) : (
              "All responses"
            )}
          </Typography>
          <IconButton className={modalClasses.close} onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        {totalResponses > 0 && (
          <Box display="flex" justifyContent="space-between" gap={2} alignItems={"center"}>
            <Box className={sharedClasses.fieldBox} flex={1}>
              <InputLabel>Search</InputLabel>
              <TextField
                fullWidth
                placeholder="Search by ID or URL Params"
                onChange={(e) => setSearchPhrase(e.target.value)}
                value={searchPhrase}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" sx={{ color: "#67676C", width: "20px" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchPhrase ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Clear search"
                        edge="end"
                        size="small"
                        onClick={() => {
                          setSearchPhrase("");
                          setDebouncedSearch("");
                        }}
                      >
                        <ClearIcon sx={{ color: "#67676C", width: "20px" }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Box>

            <Box display="flex" alignItems="center">
              <Box className={sharedClasses.fieldBox}>
                <InputLabel>
                  {selectedCount === 0
                    ? "Select response to change status"
                    : `New status for ${selectedCount} response${selectedCount > 1 ? "s" : ""}`}
                </InputLabel>
                <Select
                  fullWidth
                  value={newStatus}
                  onChange={(e) => {
                    setNewStatus(e.target.value);
                  }}
                  disabled={selectedCount === 0}
                  displayEmpty
                  renderValue={(selected) => {
                    const selectedOption = statusOptions.find((opt) => opt.value === selected);
                    return selectedOption ? (
                      renderStatusChip(selectedOption)
                    ) : (
                      <span style={{ color: "#85858B" }}>Change status</span>
                    );
                  }}
                  IconComponent={(props) =>
                    props.className?.includes("MuiSelect-iconOpen") ? (
                      <ChevronUpIcon sx={{ color: COLORS.iconColor }} />
                    ) : (
                      <ChevronDownIcon sx={{ color: COLORS.iconColor }} />
                    )
                  }
                  MenuProps={{
                    PaperProps: {
                      className: sharedClasses.innerBox,
                    },
                  }}
                >
                  {statusOptions.map((option) => (option.value != 'terminated' &&
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={{
                        py: 0.8,
                        "&.Mui-selected, &:hover, &.Mui-selected:hover": {
                          backgroundColor: option.bgcolor,
                          color: "inherit",
                          borderRadius: "8px",
                        },
                      }}
                    >
                      {renderStatusChip(option)}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Button
                sx={{ mt: 1, ml: 1 }}
                disabled={selectedCount === 0 || newStatus === ""}
                className={cx(buttonClasses.blue, buttonClasses.iconButton)}
                onClick={() => {
                  void handleUpdateResponses();
                }}
              >
                Update
              </Button>
            </Box>
            <Button
              variant="outlined"
              className={cx(buttonClasses.iconButton)}
              startIcon={
                <TuneIcon sx={{ color: appliedFilters.length > 0 ? "#fff" : COLORS.iconColor }} />
              }
              sx={{
                mt: 1,
                px: "16px",
                height: 40,
                color: appliedFilters.length > 0 ? "#fff" : COLORS.iconColor,
                bgcolor: appliedFilters.length > 0 ? COLORS.secondary : "#fff",
                "&:focus, &:hover": {
                  bgcolor: appliedFilters.length > 0 ? COLORS.secondary : "#fff",
                },
              }}
              onClick={(e) => setFilterAnchorEl(e.currentTarget)}
            >
              Filters
              {appliedFilters.length > 0 && (
                <Badge
                  badgeContent={appliedFilters.length}
                  color="default"
                  sx={{
                    ml: "18px",
                    mr: "12px",
                    "& .MuiBadge-badge": {
                      background: "#fff",
                      color: COLORS.secondary,
                    },
                  }}
                />
              )}
            </Button>
            <Button
              sx={{ mt: 1 }}
              className={cx(buttonClasses.blue, buttonClasses.iconButton)}
              onClick={() => {
                const options = {
                  status: appliedFilters || undefined,
                  search: debouncedSearch || undefined,
                  sortField: sortBy.split(":")[0],
                  sortOrder: sortBy.split(":")[1],
                };
                downloadProjectResponses(projectId, projectSupplier ? projectSupplier?.id : null, options)
                  .then((response) => {
                    const downloadUrl = window.URL.createObjectURL(response.data);
                    const link = document.createElement("a");
                    link.href = downloadUrl;
                    link.download = `${baseName}_responses.xlsx`;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(downloadUrl);
                  })
                  .catch((e) => {
                    notifySimple(["Failed to download file."], "error");
                  });
              }}
            >
              <FileDownloadIcon /> Download XLSX
            </Button>
          </Box>
        )}
      </DialogTitle>
      <DialogContent className={modalClasses.content}>
        {totalResponses === 0 ? (
          <Box sx={{ p: 3, fontSize: 18, color: COLORS.textSecondary }}>
            No Responses available
          </Box>
        ) : (
          <Table id="ResponsesTable" stickyHeader className={sharedClasses.responsesContentTable}>
            <TableHead>
              <TableRow>
                <TableCell
                  className={sharedClasses.tableHeaderCell}
                  sx={{ minWidth: "70px !important", maxWidth: "70px !important" }}
                >
                  <CustomCheckbox
                    indeterminate={selectedCount > 0 && selectedCount < rows.length}
                    checked={selectedCount > 0 && selectedCount === rows.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell
                  className={sharedClasses.tableHeaderCell}
                  sx={{ minWidth: "20px !important", maxWidth: "20px !important" }}
                >
                  #
                </TableCell>
                <TableCell className={sharedClasses.tableHeaderCell} onClick={() => handleSort("id")}>
                  Internal ID {renderSortArrow("id")}
                </TableCell>
                <TableCell
                  className={sharedClasses.tableHeaderCell}
                  onClick={() => handleSort("status")}
                >
                  Status {renderSortArrow("status")}
                </TableCell>
                <TableCell
                  className={sharedClasses.tableHeaderCell}
                  onClick={() => handleSort("inserted_at")}
                >
                  Entered {renderSortArrow("inserted_at")}
                </TableCell>
                <TableCell
                  className={sharedClasses.tableHeaderCell}
                  onClick={() => handleSort("finished_at")}
                >
                  Finished {renderSortArrow("finished_at")}
                </TableCell>
                <TableCell
                  className={sharedClasses.tableHeaderCell}
                  onClick={() => handleSort("ip_address")}
                >
                  IP {renderSortArrow("ip_address")}
                </TableCell>
                <TableCell
                  className={sharedClasses.tableHeaderCell}
                  onClick={() => handleSort("device")}
                >
                  Device {renderSortArrow("device")}
                </TableCell>
                <TableCell
                  className={sharedClasses.tableHeaderCell}
                  onClick={() => handleSort("browser")}
                >
                  Browser {renderSortArrow("browser")}
                </TableCell>
                <TableCell
                  className={sharedClasses.tableHeaderCell}
                  onClick={() => handleSort("rid")}
                >
                  RID {renderSortArrow("rid")}
                </TableCell>
                <TableCell
                  className={sharedClasses.tableHeaderCell}
                  onClick={() => handleSort("tid")}
                >
                  TID {renderSortArrow("tid")}
                </TableCell>
                <TableCell
                  className={sharedClasses.tableHeaderCell}
                // onClick={() => handleSort("params")}
                >
                  URL Params
                  {/* {renderSortArrow("params")} */}
                </TableCell>
                <TableCell
                  className={sharedClasses.tableHeaderCell}
                // onClick={() => handleSort("entered_with_params")}
                >
                  Entered with URL Params
                  {/* {renderSortArrow("entered_with_params")} */}
                </TableCell>
                <TableCell
                  className={sharedClasses.tableHeaderCell}
                  onClick={() => handleSort("forensiqScore")}
                >
                  Device Forensiq Score {renderSortArrow("forensiqScore")}
                </TableCell>
                <TableCell
                  className={sharedClasses.tableHeaderCell}
                  onClick={() => handleSort("proofOfHumanScore")}
                >
                  Proof of Human Score {renderSortArrow("proofOfHumanScore")}
                </TableCell>
                <TableCell
                  className={sharedClasses.tableHeaderCell}
                  onClick={() => handleSort("isBot")}
                >
                  Is Bot {renderSortArrow("isBot")}
                </TableCell>
                <TableCell
                  className={sharedClasses.tableHeaderCell}
                  onClick={() => handleSort("isEventUnique")}
                >
                  Is Event Unique {renderSortArrow("isEventUnique")}
                </TableCell>
                <TableCell
                  className={sharedClasses.tableHeaderCell}
                // onClick={() => handleSort("device_forensiq")}
                >
                  Device Forensiq
                  {/* {renderSortArrow("device_forensiq")} */}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((response, i) => {
                const forensiqData = getDeviceForensiqResponseProperties(response);
                const rtSessionData = response?.rt_proof_of_human
                  ? JSON.parse(response?.rt_proof_of_human)
                  : null;
                return (
                  <TableRow key={response.id} className={modalClasses.tableRowContent}>
                    <TableCell sx={{ minWidth: "70px !important", maxWidth: "70px !important" }}>
                      <CustomCheckbox
                        checked={isChecked(response.id)}
                        onClick={() => handleCheckboxClick(response.id)}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: "20px !important", maxWidth: "20px !important" }}>
                      {Math.max(0, currentPage - 1) * rowsPerPage + i + 1}
                    </TableCell>
                    <TableCell>{response.id}</TableCell>
                    <TableCell>
                      {renderStatusChip(
                        statusOptions.find((opt) => opt.value === response.status) || " - "
                      )}
                    </TableCell>
                    <TableCell>{getLocalTime(response.inserted_at).format(dateFormat)}</TableCell>
                    <TableCell>
                      {response.finished_at && getLocalTime(response.finished_at).format(dateFormat)}
                    </TableCell>
                    <TableCell>{response.ip_address}</TableCell>
                    <TableCell>{response.device.toLowerCase()}</TableCell>
                    <TableCell>{response.browser}</TableCell>
                    <TableCell>
                      {response.params.rid || response.entered_with_params?.rid || ""}
                    </TableCell>
                    <TableCell>{response.params.tid || ""}</TableCell>
                    <TableCell>
                      {Object.entries(response.params).map((el, j) => (
                        <div key={j}>{`${el[0]}: ${el[1]}\n`}</div>
                      ))}
                    </TableCell>
                    <TableCell>
                      {response.entered_with_params
                        ? Object.entries(response.entered_with_params).map((el, j) => (
                          <div key={j}>{`${el[0]}: ${el[1]}\n`}</div>
                        ))
                        : ""}
                    </TableCell>
                    <TableCell>{forensiqData.forensiqScore}</TableCell>
                    <TableCell>
                      <a href={`/respondent/${response.id}/proof-of-human`} target="_blank">{rtSessionData?.risk_score}</a>
                    </TableCell>
                    <TableCell>{forensiqData.isBot.toString()}</TableCell>
                    <TableCell>{forensiqData.isEventUnique.toString()}</TableCell>
                    <TableCell>
                      {response.device_forensiq ? (
                        <a href={`/respondent/${response.id}/device-forensiq`} target="_blank">
                          View Raw Data
                        </a>
                      ) : (
                        "No data found"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <Popover
        open={filterOpen}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            className: sharedClasses.innerBox,
            sx: {
              borderRadius: "16px",
              boxShadow: 3,
              p: 2,
              minWidth: 280,
              overflow: "hidden",
            },
          },
        }}
      >
        <Box sx={{ position: "relative", fontSize: 14, lineHeight: "20px" }}>
          <IconButton
            onClick={() => setFilterAnchorEl(null)}
            sx={{ position: "absolute", top: 6, right: 0, p: 0, color: COLORS.textMain }}
            aria-label="Close"
          >
            <ClearIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Typography variant="h6" sx={{ fontSize: 20, my: 0 }}>
            Filters
          </Typography>
          <Typography sx={{ my: 1.5, fontSize: 16 }}>Status</Typography>
          <Box>
            {statusOptions.map((status) => (
              <Box key={status.value} sx={{ display: "flex", alignItems: "center" }}>
                <CustomCheckbox
                  checked={statusFilter.indexOf(status.value) > -1}
                  onChange={() => handleStatusCheckbox(status.value)}
                />
                {renderStatusChip(status)}
              </Box>
            ))}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ReplayIcon />}
              className={cx(buttonClasses.iconButton, buttonClasses.iconButton)}
              onClick={() => {
                setAppliedFilters([]);
                setStatusFilter([]);
              }}
            >
              Clear All
            </Button>
            <Button
              onClick={() => {
                setAppliedFilters(statusFilter);
                setFilterAnchorEl(null);
              }}
              className={cx(buttonClasses.blue, buttonClasses.iconButton)}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Popover>
      {total > 0 && (
        <StickyPagination
          count={total}
          page={currentPage}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={(n) => {
            setRowsPerPage(n);
            setCurrentPage(1);
          }}
          sx={{ position: "sticky", width: "100%", px: 1 }}
        />
      )}
    </Dialog>
  );
};

export default ResponsesModal;
